import z from 'zod';

export const IMAGE_RATIOS = [
  '1:1',
  '3:4',
  '4:5',
  '4:3',
  '9:16',
  '16:9',
] as const;
export type ImageRatio = (typeof IMAGE_RATIOS)[number];

export const AI_STUDIO_IMAGE_STATUS = ['success', 'loading', 'failed'] as const;
export type AiStudioImageStatus = (typeof AI_STUDIO_IMAGE_STATUS)[number];

const API_STATUS_MAP: Record<string, AiStudioImageStatus> = {
  SUCCESS: 'success',
  PROCESSING: 'loading',
  FAILED: 'failed',
  success: 'success',
  loading: 'loading',
  failed: 'failed',
};

const normalizeStatus = z
  .string()
  .transform((v) => API_STATUS_MAP[v] ?? 'failed')
  .pipe(z.enum(AI_STUDIO_IMAGE_STATUS));

export const AiStudioImageSchema = z.object({
  id: z.string(),
  index: z.number(),
  path: z.string(),
  width: z.number(),
  height: z.number(),
  tags: z
    .union([z.array(z.string()), z.null(), z.record(z.string(), z.unknown())])
    .optional()
    .transform((v) => (Array.isArray(v) ? v : [])),
  status: normalizeStatus.default('success'),
  created_at: z.string(),
});

export const AiStudioReferenceImageSchema = z.object({
  index: z.number(),
  path: z.string(),
  type: z.string(),
  status: normalizeStatus.optional(),
});

export const AiStudioOriginImageSchema = z.object({
  index: z.number(),
  path: z.string(),
  type: z.string(),
  status: normalizeStatus.optional(),
});

export const AiStudioGallerySchema = z.object({
  id: z.string(),
  product_id: z.string(),
  aspect_ratio: z
    .string()
    .nullish()
    .transform((v) => v ?? ''),
  images: z
    .array(AiStudioImageSchema)
    .nullish()
    .transform((v) => v ?? []),
  prompt: z
    .string()
    .nullish()
    .transform((v) => v ?? ''),
  reference_images: z
    .array(AiStudioReferenceImageSchema)
    .nullish()
    .transform((v) => v ?? []),
  origin_images: z
    .array(AiStudioOriginImageSchema)
    .nullish()
    .transform((v) => v ?? []),
  created_at: z.string(),
  updated_at: z.string(),
});

export const AiStudioGalleryListSchema = z.object({
  code: z.coerce.number(),
  data: z.array(AiStudioGallerySchema),
  message: z.string(),
  status: z.unknown().optional(),
});

export type AiStudioImage = z.infer<typeof AiStudioImageSchema>;
export type AiStudioReferenceImage = z.infer<
  typeof AiStudioReferenceImageSchema
>;
export type AiStudioOriginImage = z.infer<typeof AiStudioOriginImageSchema>;
export type AiStudioGallery = z.infer<typeof AiStudioGallerySchema>;
export type AiStudioGalleryList = z.infer<typeof AiStudioGalleryListSchema>;

export type GalleryStatus = 'loading' | 'failed' | 'success';

export function deriveGalleryStatus(
  images: Pick<AiStudioImage, 'status'>[],
): GalleryStatus {
  if (images.length === 0) return 'success';
  if (images.some((img) => img.status === 'loading')) return 'loading';
  if (images.every((img) => img.status === 'failed')) return 'failed';
  return 'success';
}

export type CreateAiStudioImageRequest = {
  product_id: string;
  origin_images: string[];
  prompt: string;
  aspect_ratio: string;
  reference_images: string[];
  presets?: string[];
};

const LocalizedStringSchema = z.object({
  en: z.string(),
  ko: z.string(),
});

export const AiStudioPresetSchema = z.object({
  id: z.string(),
  category: LocalizedStringSchema,
  name: LocalizedStringSchema,
  preset_image: z.string(),
  prompt: z
    .string()
    .nullish()
    .transform((v) => v ?? ''),
});

export const AiStudioPresetListSchema = z.object({
  code: z.coerce.number(),
  data: z.array(AiStudioPresetSchema),
  message: z.string(),
  status: z.unknown().optional(),
});

export type AiStudioPreset = z.infer<typeof AiStudioPresetSchema>;

export type SaveGalleryImageToAssetRequest = {
  gallery_id: string;
  image_id?: string;
};

export type DeleteGalleryImageRequest = {
  gallery_id: string;
  image_id?: string;
};

export type CreateInpaintingRequest = {
  product_id: string;
  origin_image: string;
  mask_image: string;
  prompt: string;
};
